/**
 * Cache Service
 * Gère le cache localStorage avec TTL (Time To Live) et support multi-store
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time To Live en millisecondes
}

class CacheService {
  private defaultTTL = 5 * 60 * 1000; // 5 minutes par défaut
  private prefix = 'comma_cache_';

  /**
   * Génère une clé de cache pour un store et un type de données
   */
  private getCacheKey(storeId: number, dataType: string): string {
    return `${this.prefix}store_${storeId}_${dataType}`;
  }

  /**
   * Vérifie si une entrée de cache est valide (non expirée)
   */
  private isValid<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
    if (!entry) return false;
    const now = Date.now();
    return (now - entry.timestamp) < entry.ttl;
  }

  /**
   * Récupère des données depuis le cache
   * @param storeId - ID du store
   * @param dataType - Type de données (permissions, customFields, etc.)
   * @returns Les données en cache ou null si expiré/inexistant
   */
  get<T>(storeId: number, dataType: string): T | null {
    try {
      const key = this.getCacheKey(storeId, dataType);
      const cached = localStorage.getItem(key);
      
      if (!cached) return null;

      const entry: CacheEntry<T> = JSON.parse(cached);
      
      if (!this.isValid(entry)) {
        // Cache expiré, le supprimer
        this.remove(storeId, dataType);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.error(`[CacheService] Error reading cache for ${dataType}:`, error);
      return null;
    }
  }

  /**
   * Sauvegarde des données dans le cache
   * @param storeId - ID du store
   * @param dataType - Type de données
   * @param data - Données à mettre en cache
   * @param ttl - Time To Live en millisecondes (optionnel)
   */
  set<T>(storeId: number, dataType: string, data: T, ttl?: number): void {
    try {
      const key = this.getCacheKey(storeId, dataType);
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        ttl: ttl || this.defaultTTL
      };

      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.error(`[CacheService] Error writing cache for ${dataType}:`, error);
      // Si localStorage est plein, nettoyer les anciens caches
      this.cleanup();
    }
  }

  /**
   * Supprime une entrée de cache
   */
  remove(storeId: number, dataType: string): void {
    try {
      const key = this.getCacheKey(storeId, dataType);
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`[CacheService] Error removing cache for ${dataType}:`, error);
    }
  }

  /**
   * Supprime tout le cache d'un store
   */
  clearStore(storeId: number): void {
    try {
      const keys = Object.keys(localStorage);
      const storePrefix = this.getCacheKey(storeId, '');
      
      keys.forEach(key => {
        if (key.startsWith(storePrefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error(`[CacheService] Error clearing cache for store ${storeId}:`, error);
    }
  }

  /**
   * Supprime tous les caches expirés
   */
  cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      const prefix = this.prefix;
      
      keys.forEach(key => {
        if (key.startsWith(prefix)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const entry: CacheEntry<any> = JSON.parse(cached);
              if (!this.isValid(entry)) {
                localStorage.removeItem(key);
              }
            }
          } catch (e) {
            // Si erreur de parsing, supprimer la clé
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('[CacheService] Error during cleanup:', error);
    }
  }

  /**
   * Supprime tout le cache de l'application
   */
  clearAll(): void {
    try {
      const keys = Object.keys(localStorage);
      const prefix = this.prefix;
      
      keys.forEach(key => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[CacheService] Error clearing all cache:', error);
    }
  }

  /**
   * Supprime le cache des permissions pour un utilisateur (tous les stores).
   * À appeler au logout pour que la prochaine connexion recharge les permissions.
   */
  clearPermissionsForUser(userId: number): void {
    try {
      const keys = Object.keys(localStorage);
      const prefix = this.prefix;
      const suffix = 'permissions_user_' + userId;

      keys.forEach(key => {
        if (key.startsWith(prefix) && key.endsWith(suffix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[CacheService] Error clearing permissions cache for user:', error);
    }
  }

  /**
   * Supprime le cache layout/préférences dashboard (localStorage comma_dashboard_*).
   * À appeler au logout pour que la prochaine connexion recharge layout et préférences.
   */
  clearDashboardLayoutForAllStores(): void {
    try {
      const keys = Object.keys(localStorage);
      const dashboardPrefix = 'comma_dashboard_';

      keys.forEach(key => {
        if (key.startsWith(dashboardPrefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('[CacheService] Error clearing dashboard layout cache:', error);
    }
  }

  /**
   * Configure le TTL par défaut
   */
  setDefaultTTL(ttl: number): void {
    this.defaultTTL = ttl;
  }
}

// Export d'une instance singleton
const cacheService = new CacheService();

// Nettoyage automatique au démarrage
if (typeof window !== 'undefined') {
  cacheService.cleanup();
}

export default cacheService;

